import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { 
  XMarkIcon,
  UserGroupIcon,
  UsersIcon,
  BriefcaseIcon,
  ChartBarIcon,
  CogIcon,
  CloudIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  BookOpenIcon,
  MagnifyingGlassIcon
} from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/24/solid";

interface DocsPageProps {
  onClose?: () => void;
}

interface Module {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  sections: Section[];
}

interface Section {
  id: string;
  title: string;
  content: string;
  subsections?: Subsection[];
}

interface Subsection {
  id: string;
  title: string;
  content: string;
}

const modules: Module[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    icon: <RocketLaunchIcon className="w-6 h-6" />,
    description: "Quick start guide and initial setup",
    sections: [
      {
        id: "overview",
        title: "Platform Overview",
        content: `
# Welcome to Metatalio

Metatalio is the next-generation AI-powered recruitment platform that transforms how you find, engage, and hire top talent. Built for modern recruiting teams, we combine cutting-edge artificial intelligence with intuitive design to deliver exceptional results.

## 🚀 What Makes Metatalio Special

### AI-Powered Intelligence
Our advanced AI engine doesn't just search—it understands. Using natural language processing and machine learning, Metatalio comprehends job requirements and candidate profiles at a human level, delivering matches that traditional keyword searches simply can't achieve.

**Key AI Features:**
- **Natural Language Search**: Use conversational queries like "Find me a senior React developer with fintech experience who's open to remote work"
- **Intelligent Matching**: Our AI analyzes skills, experience, career trajectory, and cultural fit indicators
- **Predictive Analytics**: Forecast hiring success and identify top performers before they apply
- **Automated Screening**: AI-driven initial candidate evaluation and ranking

### Global Talent Network
Access the world's largest pool of qualified professionals through our integrated data sources:

- **800+ Million Profiles**: Real-time access to professional profiles across all industries
- **30+ Data Sources**: LinkedIn, GitHub, Stack Overflow, AngelList, Behance, and more
- **Real-Time Updates**: Fresh data with automatic profile updates and activity tracking
- **Global Coverage**: Candidates from 190+ countries with local market insights

### Enterprise-Grade Security
Built for security-conscious organizations with enterprise compliance requirements:

- **SOC 2 Type II Compliance**: Audited security controls and data protection
- **GDPR & CCPA Ready**: Full compliance with global privacy regulations
- **End-to-End Encryption**: All data encrypted in transit and at rest
- **Role-Based Access Control**: Granular permissions and audit trails

## 🎯 Who Uses Metatalio

### Staffing Agencies
- **Multi-Client Management**: Separate workspaces for each client
- **White-Label Branding**: Customize platform appearance for client presentations
- **Advanced Reporting**: Client-specific metrics and performance dashboards
- **Vendor Network**: Manage subcontractor relationships and submissions

### Corporate Recruiting Teams
- **Department-Specific Workflows**: Tailored processes for different business units
- **Hiring Manager Collaboration**: Streamlined approval and feedback loops
- **Compliance Tracking**: Automated EEO and regulatory compliance monitoring
- **Integration Ecosystem**: Connect with your existing HRIS, ATS, and productivity tools

### Executive Search Firms
- **Confidential Searches**: Discrete candidate identification and outreach
- **Relationship Management**: Long-term candidate relationship tracking
- **Market Intelligence**: Industry insights and compensation benchmarking
- **Client Portal**: Dedicated spaces for client collaboration and updates

## 📊 Success Metrics

Organizations using Metatalio typically see:

- **7x Faster Sourcing**: Reduce time-to-find qualified candidates by 85%
- **45% Better Match Quality**: Higher interview-to-offer ratios
- **60% Reduction in Sourcing Costs**: Lower cost-per-hire across all roles
- **90% User Adoption Rate**: Intuitive interface drives team engagement

## 🔄 Platform Architecture

### Multi-Tenant Design
- **Tenant Isolation**: Complete data separation between organizations
- **Scalable Infrastructure**: Auto-scaling to handle any volume
- **Custom Configuration**: Tenant-specific workflows and branding
- **API-First Architecture**: Extensible and integration-ready

### Modern Technology Stack
- **Cloud-Native**: Built on modern cloud infrastructure
- **Real-Time Processing**: Instant search results and live updates
- **Mobile Responsive**: Full functionality on any device
- **Progressive Web App**: App-like experience in your browser

![Platform Architecture](https://via.placeholder.com/800x400/4F46E5/FFFFFF?text=Metatalio+Platform+Architecture)

## 🎉 Getting Started Journey

### Phase 1: Foundation (Week 1)
1. **Account Setup**: Create your tenant and configure basic settings
2. **Team Onboarding**: Invite users and assign initial roles
3. **Integration Planning**: Identify key systems for connection
4. **First Search**: Complete your first AI-powered candidate search

### Phase 2: Optimization (Week 2-3)
1. **Workflow Customization**: Tailor processes to your methodology
2. **Advanced Features**: Explore analytics, automation, and reporting
3. **Team Training**: Comprehensive user training and best practices
4. **Performance Baseline**: Establish metrics for success measurement

### Phase 3: Mastery (Month 2+)
1. **Advanced AI Usage**: Master natural language search techniques
2. **Process Automation**: Implement time-saving automated workflows
3. **Analytics Deep Dive**: Leverage insights for strategic decisions
4. **Continuous Optimization**: Regular performance reviews and improvements

Ready to transform your recruitment process? Let's get started! 🚀
        `
      },
      {
        id: "quick-setup",
        title: "Quick Setup Guide",
        content: `
# Getting Started with Metatalio

Welcome! Let's get your Metatalio workspace set up and ready to transform your recruiting process. This guide will walk you through everything you need to know to get started in about 15 minutes.

## Before You Begin

Make sure you have your admin login credentials ready. If you're setting up Metatalio for your team, you'll also want to have a list of the people you'd like to invite and any integration details (like your email system or applicant tracking system credentials) handy.

## Setting Up Your Workspace

### Personalizing Your Tenant

Think of your tenant as your organization's personal workspace within Metatalio. To make it your own, navigate to the Settings menu in the top right corner, then click on **Tenant Settings**. Here's where you'll add your company details.

Start by entering your company name exactly as you'd like it to appear throughout the platform. This is what your team members will see when they log in, and what candidates will see in communications from you.

Next, choose your industry from the dropdown menu. This helps Metatalio tailor the experience to your specific needs - recruiting for a tech startup looks different from hiring for healthcare or finance.

Upload your company logo by clicking the logo placeholder. We recommend using a square image (200x200 pixels works great) with a transparent background for the best results.

You can also customize your colors to match your brand. Click on the color pickers to choose your primary and secondary colors. These will appear throughout the interface, making Metatalio feel like a natural extension of your company's tools.

### Configuring Email Communications

Metatalio sends emails on your behalf when you reach out to candidates, so let's make sure those emails come from you.

Go to **Settings > Email Configuration**. If you're not sure about the technical details here, your IT team can help you with your SMTP server information. Once configured, all outreach emails will come from your company's domain, which looks more professional and improves deliverability.

After entering your email credentials, click the **Send Test Email** button. You should receive a test message within a few minutes. If you don't see it, check your spam folder and verify your SMTP settings with your IT team.

### Inviting Your Team

Now let's get your team access to Metatalio. Click on **Team** in the main navigation, then **Invite Team Members**. You can add people one at a time or upload a CSV file if you're inviting multiple team members.

When you invite someone, you'll choose their role. Here's what each role can do:

**Recruiter** - This is your hands-on hiring team. They can search for candidates, manage submissions, and track progress. Perfect for your talent acquisition team who'll be doing the day-to-day recruiting work.

**Hiring Manager** - Give this role to the people who make final hiring decisions. They can review candidates, provide feedback, and approve or reject submissions, but they won't see all the recruiting admin features.

**Admin** - Reserve this for people who need to manage settings, users, and billing. Admins can do everything in the system.

**Read-Only** - Sometimes you have stakeholders who need visibility without making changes. This role is perfect for executives or finance team members who just need to see what's happening.

Each person you invite will receive an email with a link to set up their password and get started.

## Finding Your First Candidates

### Using the Search

Metatalio's search understands natural language, so you don't need to learn complicated search syntax. Just describe who you're looking for in plain English.

For example, instead of typing complex search operators, try something like: "Senior software engineer with React experience in the San Francisco Bay Area" or "Marketing manager with B2B SaaS background who has managed teams".

The AI will understand what you mean and find relevant candidates. You'll see results appear with a match score that shows how well each candidate fits what you're looking for.

After your initial search, you can narrow things down using the filters on the left side. Use the **Experience** slider to focus on people with specific years in the field. The **Location** filter helps you find people in specific cities or who are open to remote work. And the **Skills** checkboxes let you ensure candidates have the technical or soft skills you need.

### Understanding Match Scores

Each candidate has a match percentage. This tells you how closely they align with your search criteria. A 90% match means this person checks almost all your boxes. Scores above 75% are typically worth a closer look, while anything below 50% might be a stretch.

The match score considers their skills, experience level, location preferences, industry background, and more. It's a quick way to prioritize who to reach out to first.

### Saving Profiles You Like

When you find someone interesting, click the star icon next to their name. This marks them as a favorite and keeps them easy to find later. Your starred profiles automatically appear at the top of your list, so you can quickly come back to them when you're ready to reach out.

Use starring to keep track of standout candidates in a large list, prioritize people for immediate outreach, or highlight profiles you want to discuss with your hiring manager before moving forward.

## Connecting Your Tools

### Linking Your Applicant Tracking System

If you use another ATS, you can connect it to Metatalio so information flows between both systems automatically.

Go to **Settings > Integrations** and click **Connect** next to your ATS provider. You'll need your API credentials from that system - if you don't have them handy, reach out to their support team or your system administrator.

Once connected, candidate information syncs automatically, so you don't have to enter data twice.

### Setting Up Calendar Integration

Connect your Google or Outlook calendar so Metatalio can help schedule interviews without the back-and-forth emails.

In **Settings > Calendar**, click **Connect Calendar** and follow the prompts to authorize access. Metatalio will be able to see your availability and suggest interview times that work for everyone.

## You're All Set!

That's it - you're ready to start using Metatalio! Your workspace is configured, your team has access, and you know how to find and save candidates.

If you run into questions, click the support icon in the bottom right corner anytime. Our team is here to help you succeed.

Over the next few days, try creating your first job requisition, doing a few searches to get comfortable with the AI, and reaching out to a candidate or two. The more you use Metatalio, the better it gets at understanding what you're looking for.

Welcome aboard!
        `
      },
      {
        id: "search-candidates"      },
      {
        id: "search-candidates",
        title: "Searching for Candidates",
        content: `
# Finding the Right Candidates

Searching for candidates in Metatalio is designed to feel natural and intuitive. You don't need to learn complex search syntax or remember specific keywords - just describe who you're looking for in plain English, and let the AI do the heavy lifting.

## How Search Really Works

When you type a search query, Metatalio's AI doesn't just match keywords. It understands context, relationships, and even what you might mean when you're not entirely specific. Think of it less like a Google search and more like describing your ideal candidate to an experienced recruiter who instantly knows millions of people.

For example, if you search for "senior engineer who knows React," the AI understands you're likely looking for someone with several years of front-end development experience, probably comfortable with JavaScript and modern web development patterns, even if those exact words aren't in their profile.

### Writing Effective Searches

The best searches are specific without being overly rigid. Here's what works well:

**Instead of this:** "engineer"  
**Try this:** "software engineer with 5+ years experience in web development"

**Instead of this:** "sales"  
**Try this:** "enterprise sales representative who has sold B2B SaaS products"

**Instead of this:** "manager marketing"  
**Try this:** "marketing manager with team leadership experience in the tech industry"

The more context you provide, the better the results. Include things like years of experience, specific technologies or skills, industry background, or the type of company they've worked at before.

## Understanding Match Scores

Every candidate gets a match score from 0-100% that shows how well they align with your search. Here's how to interpret those numbers:

**90-100%**: This is a near-perfect match. These candidates check almost every box in your search and should be your first priority for outreach.

**75-89%**: These are strong matches with most of the qualifications you're looking for. They're definitely worth reviewing and likely worth contacting.

**60-74%**: Solid candidates who meet many of your requirements. They might be missing one or two things from your ideal profile, but could still be great fits.

**Below 60%**: These candidates have some relevant experience but may be missing key qualifications. Review them if you're widening your search or being flexible on requirements.

The match score considers their entire profile - skills, experience level, job titles, industries they've worked in, education, location, and more. It's a starting point for your evaluation, not a final verdict.

## Using Filters to Refine Results

After your initial search, you'll see filters on the left side of your results. These help you narrow down a large list to the most relevant candidates.

### Experience Level
Use the slider to focus on candidates with specific years of experience. Moving it to "5-8 years" immediately filters out both junior candidates and those who might be over-qualified or looking for more senior roles.

### Location
Click on the location filter to see only candidates in specific cities or regions. You can select multiple locations if you're hiring for several offices, or choose "Remote" to find candidates who are open to working from anywhere.

### Skills
The skills checkboxes show you the most common skills in your search results. Click the ones that are must-haves for your role. For example, if you searched for data scientists and see both "Python" and "R" listed, but you specifically need Python experience, check that box.

### Industry Experience
Sometimes the industry someone has worked in matters as much as their role. If you're hiring for a healthcare company, you might prioritize candidates who've worked in healthcare before because they already understand the unique challenges and regulations.

### Company Size
This filter helps you find people who've worked at similar-sized companies. Startups operate differently than enterprises, and this helps you find candidates who'll be comfortable in your environment.

## Saving and Organizing Candidates

### Starring Profiles
When you find a candidate who stands out, click the star icon next to their name. This does two important things:

First, it marks them as a favorite so you can easily find them later. Starred profiles automatically float to the top of your list, making them quick to access when you're ready to take action.

Second, it gives you a way to track candidates across multiple searches. If you're working on several roles or comparing candidates over time, starring helps you remember who caught your attention and why.

Use starring to keep track of standout candidates in a long list, prioritize people for immediate outreach, or flag profiles you want to discuss with your hiring manager before proceeding.

### Creating Lists
Beyond starring individual profiles, you can create custom lists to organize candidates by project, role, or any other criteria that makes sense for you.

For example, you might create:
- "Q4 Engineering Hires" for your upcoming hiring push
- "Future Opportunities" for great candidates who aren't right for current openings
- "Pipeline - Senior Roles" for experienced candidates to nurture over time

To create a list, select the candidates you want to include (there's a checkbox next to each profile), then click "Add to List" and either choose an existing list or create a new one.

## Viewing Candidate Profiles

Click any candidate's name to see their full profile. Here's what you'll find:

### Contact Information
Email addresses, phone numbers, LinkedIn profiles, and any other contact methods we have for the candidate. Some candidates include multiple email addresses - personal and professional - so you have options for outreach.

### Work History
A timeline of their career showing each role, company, and the duration. Pay attention to career progression - are they moving into more senior positions? How long do they typically stay at a company? These patterns tell you a lot about their career trajectory and stability.

### Skills and Expertise
Both hard skills (like programming languages or software tools) and soft skills (like leadership or communication). The skills section also shows proficiency levels when that information is available.

### Education
Degrees, certifications, and training programs. For some roles, education matters a lot. For others, practical experience counts more. This section helps you evaluate candidates based on what matters for your specific position.

### Projects and Accomplishments
Many profiles include specific projects they've worked on or accomplishments from their career. This is gold for understanding what they've actually done versus just where they've worked.

## Advanced Search Techniques

### Boolean Operators (Optional)
While conversational search works for most needs, sometimes you want precise control. You can use AND, OR, and NOT to combine criteria:

"(Java OR Python) AND cloud AND NOT intern"

This finds candidates with either Java or Python experience who also have cloud experience, excluding anyone whose profile mentions intern or internship.

### Saved Searches
If you frequently look for the same type of candidate - like React developers or sales managers - save your search. Click "Save Search" after running it, give it a name, and it'll appear in your sidebar for one-click access later.

Even better, you can enable notifications for saved searches. When new candidates matching your criteria are added to the system, you'll get an email alert. This is perfect for ongoing hiring needs where you want to catch qualified candidates as soon as they're available.

### Search History
All your recent searches are automatically saved and accessible from the search bar. Click the clock icon to see your search history and quickly re-run a previous search.

## Tips for Better Results

**Be specific about what matters most.** If years of experience is critical, include it in your search. If a specific skill is non-negotiable, mention it explicitly.

**Use industry terminology.** If you're hiring for roles with specific jargon or certifications (like "AWS Solutions Architect" or "PMP certified"), include those terms. The AI recognizes them and prioritizes candidates who use the same language.

**Consider alternatives.** Instead of only searching for "software engineer," try "developer OR programmer OR software engineer" to cast a wider net with similar roles.

**Search by company if helpful.** Looking for someone with experience at a particular company? Include it: "former Google product manager" or "ex-Amazon engineer."

**Refine, don't restart.** If your first search returns too many results or not quite the right people, use filters to refine rather than starting over with a completely new search.

The more you search, the better you'll get at phrasing queries that return exactly what you need. Metatalio learns from how you interact with results too, getting smarter about what you're really looking for over time.
        `
      },
      {
        id: "advanced-search",
        title: "Advanced Search Features",
        content: `
Coming soon! We're working on detailed documentation for advanced search features including Boolean operators, saved searches, and AI-powered recommendations.
        `
      }
    ]
  },
  {
    id: "tenants",
    name: "Tenant Management",
    icon: <UserGroupIcon className="w-6 h-6" />,
    description: "Multi-tenant architecture and organization management",
    sections: [
      {
        id: "tenant-overview",
        title: "Tenant System Overview",
        content: `
# Understanding Tenants in Metatalio

If you're new to Metatalio, you might be wondering what a "tenant" actually is. Think of it as your organization's private workspace within the platform - completely separate from every other company using Metatalio.

Whether you're a single company with different departments, a recruiting agency managing multiple clients, or an enterprise with complex organizational structures, tenants give you the flexibility and security you need.

## How Tenants Work

Imagine Metatalio as a large office building. Each tenant is like having your own private floor with locked doors. You can see and access everything on your floor, but you can't see what's happening on other floors, and they can't see what you're doing either.

This means your candidate data, job requisitions, team members, settings, and everything else are completely isolated from other organizations using the platform. It's not just separate folders in a shared space - it's genuinely separate environments at the database and application level.

## Different Ways to Use Tenants

### Single Company Setup

Most organizations start with a straightforward approach: one tenant represents their entire company. Everyone in your organization works within this tenant, and you might create different departments or teams within it to organize your work.

For example, if you're TechCorp, you'd have one TechCorp tenant. Your engineering recruiters, sales team hirers, and HR administrators all work within this tenant, but you can give them different permissions and visibility based on their roles.

### Multi-Department Organizations

As companies grow, they often want more separation between departments. Maybe your engineering team has completely different hiring processes than your sales team, or your HR policies require strict data boundaries between business units.

In this case, you might create separate tenants for each major department. Engineering gets its own tenant, Sales gets its own, Operations gets its own, and so on. Each department operates independently while you maintain umbrella visibility and control as needed.

### Staffing and Recruiting Agencies

If you're an agency serving multiple clients, tenants become incredibly powerful. Each of your clients gets their own dedicated tenant, giving them a white-labeled experience that feels like their own recruiting platform.

Your Client A sees only their candidates, their job requisitions, and their team members. Client B sees only their data. They never see each other's information, and you maintain the ability to manage all clients from your agency's perspective.

This is perfect for maintaining confidentiality, meeting client-specific compliance requirements, and providing a premium, customized experience to each client.

## Security and Data Isolation

When we say tenants are isolated, we mean it at every level:

### Your Data is Truly Private

Each tenant uses completely separate database schemas. It's not just filtering data with tags - it's genuinely separate storage. If someone somehow bypassed every security layer (which they can't), they still wouldn't find another tenant's data because it literally doesn't exist in the same place.

### Encryption and Access Control

Every tenant has its own encryption keys. Your data is encrypted both when it's stored and when it's transmitted. Only people you explicitly give access to - by inviting them to your tenant and assigning them appropriate roles - can see any of your information.

### Compliance Made Simple

Different organizations have different compliance requirements. Maybe you need to meet GDPR standards because you recruit in Europe. Maybe you're bound by CCPA in California. Or perhaps you have industry-specific regulations like HIPAA for healthcare.

Tenant isolation makes compliance straightforward because you can configure each tenant to meet its specific requirements without affecting others. Your European client's tenant can enforce GDPR data retention policies while your US-based client focuses on CCPA compliance.

## Managing Your Tenant

### Initial Setup

When you first access your tenant, you'll want to customize it to match your organization. Head to the settings area (you'll find it in the top right corner) and start with these basics:

**Organization Profile**: Add your company name, industry, logo, and contact information. This appears throughout the platform and in communications to candidates and team members.

**Branding**: Choose colors that match your brand. If you're an agency setting up a client tenant, use their brand colors to give them a seamless experience.

**Email Settings**: Configure how emails from the platform appear. They should come from your domain and look like they're from your team, not a generic system.

### Adding Team Members

Once your tenant is configured, invite your team. Click on the Team or Users section and add people one by one, or upload a spreadsheet if you're adding many at once.

For each person, you'll assign them a role that determines what they can do:

**Admins** can do everything - manage settings, invite users, access all data, and configure integrations. Only give this role to people who truly need full control.

**Recruiters** handle day-to-day hiring - searching for candidates, managing submissions, tracking progress. This is your core hiring team.

**Hiring Managers** review candidates and make decisions on specific requisitions, but don't need to see everything happening across the organization.

**Read-Only Users** can view information but can't make changes. Perfect for executives or stakeholders who need visibility without the risk of accidental edits.

### Connecting Your Tools

Most organizations already use other tools for recruiting, communication, or HR management. You can connect those to your Metatalio tenant so information flows automatically:

**Applicant Tracking Systems**: Connect your existing ATS so candidate data syncs between systems. No more double-entry or version conflicts.

**Email and Calendar**: Link your email system and calendar so scheduling and communication happen seamlessly within Metatalio.

**Communication Platforms**: Connect Slack or Microsoft Teams to get notifications about candidate activity, new submissions, or  important updates right where your team already works.

Setting up integrations usually takes just a few minutes. You'll need API credentials or authorization from your other tools, which your IT team can help with if you're not sure where to find them.

## Growing and Adapting

Your tenant configuration isn't set in stone. As your needs change, you can:

Add or remove team members as your hiring team evolves. Adjust permissions when people change roles. Create new custom fields to track information specific to your process. Update integrations as you adopt new tools.

Everything is designed to be flexible because we know your recruiting needs will evolve over time.

## Getting Help

If you run into questions about how to set up or manage your tenant, the support team is here to help. Click the support icon to get in touch, or check the documentation for specific how-to guides.

Remember, your tenant is your workspace - make it work the way you work.
        `
      },
      {
        id: "tenant-config",
        title: "Tenant Configuration",
        content: `
# Configuring Your Tenant

Once your tenant is set up, you'll want to customize it to match your organization's specific needs and branding.

## Accessing Configuration Settings

To configure your tenant, click on **Settings** in the main navigation, then select **Tenant Settings**. This brings you to a dashboard where you can control every aspect of how your tenant operates.

## Basic Configuration

### Company Information

Start with the basics - your company details. These appear throughout the platform and in communications with candidates:

**Company Name**: This is what appears in the header, emails, and anywhere your organization is referenced. Make sure it's exactly how you want it displayed.

**Industry**: Choose the industry that best describes your business. This helps Metatalio optimize features and suggestions for your specific sector. If you're in multiple industries, choose your primary one.

**Company Size**: Select your current employee count range. This helps us provide appropriate benchmarks and recommendations.

**Location**: Your headquarters or primary office location. This sets defaults for things like time zones and regional settings.

### Contact Information

Set up how people can reach your team:

**Support Email**: Where candidates and team members should send questions. This is used in automated emails and help resources.

**Phone Number**: Optional, but helpful for candidates who prefer to call with questions.

**Website**: Your company's main website. This appears in job postings and candidate communications.

## Branding and Appearance

### Logo and Visual Identity

**Upload Your Logo**: Click the logo placeholder to upload your company logo. For best results:
- Use a PNG file with a transparent background
- Recommended size: 200x200 pixels minimum
- Square format works best
- File size under 2MB

Your logo appears in the header, emails to candidates, and throughout the interface.

### Color Customization

Make Metatalio feel like part of your brand by customizing colors:

**Primary Color**: This is your main brand color. It's used for buttons, links, and key interface elements. Choose a color that's readable and represents your brand well.

**Secondary Color**: A complementary color for accents and highlights. This adds visual interest while maintaining brand consistency.

Click the color pickers to choose colors, or enter hex codes if you have specific brand colors (#FF5733 format).

As you make changes, you'll see a preview update in real-time so you can ensure everything looks right before saving.

## Email Configuration

Setting up email properly ensures communications to candidates come from your domain and look professional.

### SMTP Settings

You'll need these details from your IT team or email provider:

**SMTP Server**: The address of your email server (like smtp.gmail.com or mail.yourcompany.com)

**Port**: Usually 587 for TLS or 465 for SSL. Check with your email provider.

**Username**: Your email account username, often your full email address.

**Password**: Your email password or application-specific password.

**Encryption**: Choose TLS (recommended) or SSL for secure communications.

### Testing Your Setup

After entering your SMTP details, click **Send Test Email**. You should receive a test message within a few minutes. If you don't:

1. Check your spam/junk folder
2. Verify the SMTP settings are correct
3. Make sure your email provider allows SMTP access (some require you to enable it)
4. Contact your IT team if you're unsure about the settings

### Email Templates

Customize how emails to candidates look and feel:

**From Name**: How your emails appear in candidates' inboxes. Usually "[Your Company] Recruiting" or similar.

**Reply-To Address**: Where candidates' replies go. This can be different from the sending address.

**Email Signature**: Add a signature that appears at the bottom of emails. Include your company name, website, and any legal disclaimers.

## Workflow Settings

### Default Processes

Set up standard workflows that apply to all requisitions unless specified otherwise:

**Approval Requirements**: Define who needs to approve new requisitions before recruiting begins. You might require hiring manager approval, then department head sign-off.

**Interview Stages**: Create your standard interview process. Common stages include:
- Phone Screen
- First Interview  
- Technical Assessment
- Team Interview
- Final Interview
- Offer

You can always customize this for individual requisitions, but having defaults saves time.

### Notification Preferences

Control who gets notified about what:

**New Candidate Notifications**: When someone applies or is added to the system, who should know? 

**Interview Reminders**: How far in advance should interviewers be reminded?

**Status Updates**: Alert hiring managers when candidates move between stages.

You can set these globally and let individual users override them in their personal preferences.

## Compliance and Privacy

### Data Retention

Define how long candidate data is kept:

**Active Candidates**: Usually retained indefinitely while they're in your pipeline.

**Rejected Candidates**: Set a retention period (common: 1-2 years) before automatic deletion.

**Hired Candidates**: These typically move to your HRIS system. Configure whether they're retained in Metatalio.

### Privacy Settings

**GDPR Compliance**: If you recruit in Europe, enable GDPR features including automated consent tracking and right-to-be-forgotten capabilities.

**Data Export**: Allow users to request their data exports for compliance purposes.

**Consent Management**: Track and store candidate consent for data processing.

## Advanced Settings

### Custom Fields

Create additional fields specific to your needs:

Click **Add Custom Field** and define:
- Field name (what users see)
- Field type (text, number, dropdown, date, etc.)
- Required or optional
- Where it appears (candidate profiles, requisitions, etc.)

For example, you might add:
- "Clearance Level" for government contractors
- "Specialty" for healthcare recruiting
- "Tech Stack" for engineering roles

### Integration Webhooks

If you have custom systems, set up webhooks to receive real-time notifications when events happen in Metatalio:

- New candidate added
- Candidate status changed
- Interview scheduled
- Offer extended

Provide your webhook URL and select which events to monitor.

## Saving Changes

As you configure settings, remember to click **Save Changes** at the bottom of each section. Some changes take effect immediately, while others (like email settings) might take a few minutes to propagate.

If you make a mistake, don't worry - you can always come back and adjust settings. Nothing is permanent, and you can iterate on your configuration as your needs evolve.

## Getting Help

If you're unsure about any configuration option, hover over the information icon (ℹ️) next to it for a quick explanation. For more detailed help, click the support button in the bottom right corner to chat with our team.

Most organizations start with basic configuration and gradually add complexity as they become more comfortable with the platform. There's no rush to configure everything at once - focus on what you need to get started and expand from there.
        `
      }
    ]
  },
  {
    id: "requisitions",
    name: "Requisition Management",
    icon: <BriefcaseIcon className="w-6 h-6" />,
    description: "Job requisition creation, tracking, and workflow management",
    sections: [
      {
        id: "requisition-overview",
        title: "Creating and Managing Requisitions",
        content: `
# Managing Job Requisitions

A requisition in Metatalio represents an open position you're trying to fill. It's more than just a job description - it's the central hub for everything related to that hire: candidates you're considering, interview feedback, collaboration with hiring managers, and tracking progress from opening to offer.

## Creating Your First Requisition

When you're ready to start filling a position, head to the Requisitions section and click "Create New Requisition." You'll see a form that walks you through all the details needed to define the role.

### The Basics

Start with the foundational information:

**Job Title**: Be specific and clear. "Senior Front-End Engineer" is better than just "Engineer" because it helps candidates understand the level and focus of the role immediately.

**Department**: Which team is this person joining? This helps route approvals and keeps similar requisitions organized together.

**Hiring Manager**: Who's going to make the final decision on this hire? This person will receive notifications about new candidates and be involved in the interview process.

**Location**: Where will this person work? You can specify a city, mark it as remote, or list multiple locations if there's flexibility.

**Employment Type**: Full-time, part-time, contract, or temporary? This affects everything from how you search for candidates to how you structure offers.

### Defining the Role

Now describe what the job actually entails:

**Job Description**: This is what candidates will see, so make it compelling. Explain what the person will do, what problems they'll solve, and why it's an exciting opportunity. Talk about the team they'll join and the impact they'll have.

**Requirements**: What must someone have to be considered? Years of experience, specific skills, education requirements, certifications - anything non-negotiable goes here.

**Preferred Qualifications**: What would be nice to have but isn't strictly required? Maybe you'd love someone with industry experience, but it's not a deal-breaker. List those things here.

**Key Responsibilities**: Bullet out the main things this person will do day-to-day. This helps candidates self-assess whether the role is right for them.

### Compensation and Benefits

Being transparent about compensation helps you attract the right candidates and avoid wasting time on people whose expectations don't align with what you can offer.

**Salary Range**: Enter the minimum and maximum you're willing to pay. This doesn't get shared publicly unless you choose to include it in your job posting, but it helps Metatalio match you with candidates whose expectations fit your budget.

**Benefits**: Health insurance, retirement matching, equity, unlimited PTO - whatever you offer, list it here. Benefits can be just as important as salary for many candidates.

**Bonuses and Equity**: If there are performance bonuses, sign-on bonuses, or equity grants available, mention them. These can be major selling points.

### Workflow and Approval Process

Different organizations have different processes for opening and filling positions. Metatalio adapts to yours:

**Approval Requirements**: Does this requisition need approval before you start recruiting? You can set up approval workflows where hiring managers, department heads, or finance teams review and approve the requisition before it goes live.

**Interview Process**: Define the stages each candidate will go through. Maybe it's a phone screen, technical interview, team interview, and final conversation. Outlining this upfront helps everyone on the team understand what's expected.

**Target Start Date**: When do you need this person to start? This helps prioritize your recruiting efforts and sets expectations with candidates.

## Tracking Candidates in a Requisition

Once your requisition is active, you'll start adding candidates to it. This is called "submitting" or "assigning" candidates to a requisition.

### Adding Candidates

When you find someone who looks like a good fit for your open role, you'll submit them to the requisition. Click on the candidate's profile, find the requisition in your list, and click "Submit to Requisition."

They'll appear in the requisition's pipeline, ready for review and next steps. You can also add notes about why you think they're a good fit, which helps hiring managers understand your thinking.

### Pipeline Stages

Each requisition has a pipeline that tracks where candidates are in the process. Common stages include:

**New**: Just submitted, not yet reviewed  
**Screening**: Initial review happening  
**Phone Interview**: Scheduled or completed phone screen  
**Technical Interview**: Technical evaluation in progress  
**Team Interview**: Meeting with the team  
**Final Interview**: Last step before decision  
**Offer**: Offer extended  
**Hired**: Offer accepted, they're joining the team  
**Rejected**: Not moving forward

You can customize these stages to match your process exactly. Drag candidates from one stage to the next as they progress, giving everyone real-time visibility into where things stand.

### Collaboration and Feedback

Requisitions are collaborative by nature. Multiple people need to weigh in on candidates, schedule interviews, and make decisions together.

**Interview Feedback**: After each interview, interviewers can submit structured feedback directly in Metatalio. They'll answer questions you've set up, rate the candidate in different areas, and provide written commentary.

**Internal Notes**: Have thoughts about a candidate you want to share with the team? Add internal notes that everyone working on the requisition can see. These never go to the candidate - they're just for your team.

**@Mentions**: Tag specific team members in notes to get their attention or ask for their input on a particular candidate.

### Communicating with Candidates

Throughout the process, you'll communicate with candidates about their status and next steps. Metatalio makes this easy:

**Email Templates**: Create templates for common messages like "Thanks for applying," "Let's schedule an interview," or "We've decided to move forward with other candidates." These save time and ensure consistent communication.

**Automated Notifications**: Set up automatic emails when candidates move from one stage to another. For example, when someone reaches "Phone Interview," they automatically get an email about scheduling.

**Personal Touches**: While templates are efficient, you can always customize messages for individual candidates to add a personal touch.

## Managing Multiple Requisitions

If you're hiring for several roles at once, staying organized becomes critical.

### The Requisitions Dashboard

Your requisitions dashboard shows all open positions at a glance. You'll see:

- How many candidates are in each requisition
- What stage most candidates are in
- How long the requisition has been open
- Who's responsible for each requisition
- Priority levels you've assigned

This bird's-eye view helps you allocate your time effectively and spot requisitions that need attention.

### Filtering and Sorting

With many open requisitions, you'll want to filter your view:

- Show only your assigned requisitions
- Filter by department or hiring manager
- Sort by priority or date opened
- View only requisitions with candidates ready for review

### Priority Levels

Not all hires are equally urgent. Mark critical hires as "High Priority" so they stand out visually and you focus energy there first. Lower-priority roles can be marked accordingly, helping you balance your attention across everything you're working on.

## Reporting and Analytics

Understanding how your requisitions are performing helps you improve your process over time.

### Time-to-Fill Metrics

How long does it take to fill positions? Metatalio tracks this automatically, showing you the time from opening a requisition to extending an accepted offer. If some requisitions take much longer than others, that's a signal to investigate why.

### Candidate Source Analysis

Where are your best candidates coming from? Are you finding people through search, or are they applying through job posts? Are referrals performing better than other sources? This data helps you invest time and resources in the channels that actually work.

### Pipeline Health

How many candidates are in each stage across all your requisitions? If you have tons of people in "New" but very few making it to "Interview," that might indicate your screening criteria need adjustment. If you have many people stuck at a particular interview stage, that stage might be a bottleneck.

## Closing Requisitions

When you've successfully filled a position (congratulations!), you'll close the requisition. This moves it to your archive but preserves all the data, candidate information, and history for future reference.

Mark the successful candidate as "Hired" and the system will prompt you to close the requisition. Candidates who weren't selected can be automatically moved to your general pipeline for future opportunities, so you don't lose those relationships.

If a requisition is closed for other reasons - budget cuts, restructuring, hiring freeze - you can close it without marking anyone as hired. The data is still preserved for when things change.

## Tips for Requisition Success

**Be specific in your requirements.** Vague requisitions lead to lots of mismatched candidates and wasted time. The clearer you are about what you need, the better results you'll get.

**Update your pipeline regularly.** Stale requisitions with outdated candidate statuses frustrate everyone. Set aside time weekly to move candidates through stages and update statuses.

**Communicate promptly with candidates.** Even if it's just "We're still reviewing applications," any communication is better than silence. Candidate experience matters for your employer brand.

**Use data to refine your process.** If something isn't working - requisitions taking too long to fill, candidates dropping out at a specific stage - look at the data to understand why and adjust.

Creating great requisitions and managing them effectively is the foundation of successful recruiting. Take the time to set them up right, and the rest of the hiring process becomes much smoother.
        `
      },
      {
        id: "requisition-workflow",
        title: "Workflow Automation",
        content: `
Coming soon! We're preparing comprehensive guides on automating your requisition workflows, including approval chains, status triggers, and notification automation.
        `
      }
    ]
  },
  {
    id: "candidates",
    name: "Candidate Management",
    icon: <UsersIcon className="w-6 h-6" />,
    description: "Complete candidate lifecycle management and engagement",
    sections: [
      {
        id: "candidate-overview",
        title: "Working with Candidates",
        content: `
# Managing Candidates Throughout the Process

Candidates are at the heart of everything in Metatalio. From the moment you discover someone who might be a good fit to the day they accept an offer (or you decide to pass), managing those relationships effectively makes all the difference in your hiring success.

## Understanding Candidate Profiles

Every candidate in Metatalio has a comprehensive profile that brings together all their information in one place.

### What's in a Profile

When you open a candidate's profile, you'll see everything you need to evaluate them:

**Basic Information**: Name, current location, contact details (email, phone, LinkedIn). Some candidates provide multiple ways to reach them, which is helpful if one method doesn't get a response.

**Current Position**: Where they work now, what they do, and how long they've been there. This gives immediate context about their career level and current situation.

**Work History**: A timeline of everywhere they've worked, showing progression and patterns. Are they advancing to bigger roles and responsibilities? How long do they typically stay at companies? These patterns tell you a lot.

**Skills and Expertise**: Both hard skills (programming languages, tools, certifications) and soft skills (leadership, communication, problem-solving). The profile often includes proficiency levels when that data is available.

**Education**: Degrees, certifications, training programs, and any notable academic achievements.

**Projects and Accomplishments**: Specific things they've built or achieved. This is incredibly valuable for understanding what they can actually do beyond just their job titles.

**Your Notes and Tags**: Anything your team has added to the profile - notes from conversations, tags for easy categorization, internal ratings or feedback.

### The Activity Timeline

At the bottom of each profile, you'll find a complete history of every interaction with this candidate: when they were added to the system, when someone viewed their profile, when they were submitted to requisitions, email conversations, interview feedback, and any status changes.

This timeline helps you understand the complete relationship with the candidate. If you're picking up someone else's work, you can quickly get up to speed on everything that's happened.

## Discovering New Candidates

There are several ways candidates enter your system:

### Through Search

Most candidates you'll find by searching Metatalio's database. When you run a search, you're tapping into profiles that have been aggregated from across the web, professional networks, and other sources. Finding someone through search means they're probably not actively looking for a job right now, so you'll need to reach out and spark their interest.

### From Applications

When you post job openings publicly (on your website, job boards, or social media), people will apply. These applications automatically create candidate profiles in Metatalio and associate them with the relevant requisition.

These candidates are actively looking, which changes your approach. They've already expressed interest, so your conversations can move faster.

### Referrals

When your team members or other candidates refer someone, those referrals become profiles too. Referrals often have higher success rates because they come with built-in validation - someone you trust vouches for them.

### Manual Entry

Sometimes you meet great people at conferences, through networking, or in other contexts. You can manually create profiles for these people to keep track of them for future opportunities.

## Organizing and Tagging Candidates

With potentially thousands of candidates in your system, organization is critical.

### Tags

Tags are your flexible organizing tool. You can create whatever tags make sense for your workflow:

"Engineering-Backend", "Sales-Enterprise", "Great-Culture-Fit", "Immediate-Availability", "Follow-Up-Q4", "Referred-By-Sarah"

Use tags to mark candidates in ways that help you find them later. You can filter your entire candidate list by tags, making it easy to pull up specific groups.

### Lists

While tags are great for characteristics and categories, lists are perfect for collections of candidates you're actively working with:

"Top Picks for Senior Engineer Role"  
"Pipeline for Future Product Manager Openings"  
"Candidates to Contact This Week"

Lists are dynamic - you can add and remove candidates as situations change, and you can have the same candidate on multiple lists if needed.

### Star Ratings

When you review a candidate, you can give them a star rating (1-5 stars). This is a quick way to indicate quality or fit. You might reserve 5-star ratings for exceptional candidates you'd hire immediately if you had an opening, while 3-stars might be solid candidates worth keeping in mind for appropriate future roles.

Star ratings are subjective and personal, which is fine. They're about helping you quickly sort and prioritize your own work.

## Reaching Out to Candidates

Once you've identified candidates you want to talk to, it's time to make contact.

### Email Outreach

Most initial outreach happens via email. Metatalio provides templates to speed up common messages while still allowing personalization:

**Introduction Messages**: "Hi, I came across your profile and I'm impressed by your background in..."

**Role-Specific Outreach**: "We're hiring for a Senior Data Scientist role that seems aligned with your experience..."

**Future Opportunity Building**: "While we don't have an immediate opening that matches your background, I'd love to connect for future possibilities..."

Always customize these templates. Using a candidate's name, mentioning a specific project or achievement from their profile, or referencing why you think they're a fit makes a huge difference in response rates.

### Tracking Conversations

When you send an email from Metatalio, it's automatically logged in the candidate's timeline. Replies come back into the system too, so you have a complete thread of your conversation right in their profile.

If you talk to a candidate outside Metatalio - a phone call, a LinkedIn message, meeting them at an event - you can add notes to their profile to keep that record complete. This is crucial if multiple people on your team interact with the same candidate at different times.

### Following Up

Not everyone responds to your first email. That's normal. Setting follow-up reminders helps ensure you don't let good candidates slip away just because of bad timing.

If you don't hear back after a week, a friendly follow-up often works: "I know inboxes get busy. I wanted to make sure my previous note didn't get lost..."

After two or three attempts with no response, it's usually time to move on and maybe try again in a few months.

## Moving Candidates Through the Pipeline

Once a candidate is engaged and interested in a specific role, they enter your recruiting pipeline for that requisition.

### The Candidate Journey

Most candidates go through some version of this journey:

**Initial Contact** → **Screening Call** → **First Interview** → **Additional Interviews** → **Offer** → **Hired**

Along the way, many will exit the process for various reasons - they're not interested, they're not qualified after deeper evaluation, they accept another offer, or you decide to pursue other candidates.

### Stage Management

As candidates progress, you'll move them through stages in your pipeline. This usually happens in the requisition view, where you can see all candidates for a given role organized by their current stage.

Dragging a candidate from "Phone Screen" to "Technical Interview" updates their status and can trigger automated actions like sending them information about the next step or notifying the interviewer.

### Interview Scheduling

Metatalio can integrate with your calendar system to make scheduling painless. When it's time to schedule an interview:

1. The system checks availability for everyone who needs to participate
2. Suggests time slots that work for everyone
3. Sends calendar invites automatically
4. Sends reminders before the interview

This eliminates the endless email back-and-forth of "Does Thursday at 2pm work? No? How about Friday at 10am?"

### Collecting Feedback

After each interview, everyone who participated should submit feedback while it's fresh. Interview feedback forms can be customized but typically include:

- Overall impression (thumbs up, neutral, thumbs down)
- Specific competencies evaluated (technical skills, communication, culture fit, etc.)
- Written comments about strengths and concerns
- Recommendation (advance to next round, maybe, or no)

All this feedback lives in the candidate's profile associated with the specific requisition, so decision-makers can review everything in one place before making the final call.

## Making Decisions

Eventually, you'll need to decide whether to extend an offer or pass on a candidate.

### Extending Offers

When you're ready to make an offer, you'll create it in Metatalio with all the details: salary, start date, equity, benefits, and any other terms. This creates a record of exactly what was offered and when.

You can send the offer formally through the system, and track whether the candidate has accepted, is negotiating, or has declined.

### Declining Candidates

Not everyone will be the right fit, and that's okay. When you decide not to move forward with a candidate, update their status appropriately and send them a thoughtful decline message.

Being prompt and respectful in declines preserves your employer brand and keeps the door open for future opportunities. Today's "not quite right" candidate might be perfect for a role six months from now.

### Keeping Candidates Warm

Just because someone wasn't right for this specific role doesn't mean you should lose track of them. Move them to a nurture list, tag them for future consideration, and check in occasionally.

"Hi Sarah, I know we spoke about the engineering manager role last quarter. While we went with another candidate for that position, I wanted to let you know we're opening a director-level role in Q3 that might be a better fit..."

These warm relationships often convert to great hires down the line.

## Data Privacy and Compliance

Candidate data is sensitive, and Metatalio helps you handle it responsibly.

### Consent and Transparency

Make sure candidates know they're in your system and how you'll use their information. When someone applies for a role, they're giving implicit consent. When you source someone who didn't apply, best practice is to tell them you've added them to your database and give them an option to opt out.

### Right to be Forgotten

In many jurisdictions, candidates have the right to request deletion of their data. Metatalio makes this simple - you can fully delete a candidate profile, removing all their information from the system permanently.

### Data Retention

You can configure retention policies that automatically remove old candidate data after a certain period. This helps you stay compliant with regulations while still maintaining relationships with candidates you want to keep in your pipeline.

## Tips for Candidate Management Excellence

**Keep profiles updated.** When you learn new information about a candidate - they changed jobs, gained a certification, their situation changed - add it to their profile. Fresh data leads to better matching and outreach.

**Be human in your outreach.** Templates are useful, but candidates can tell when you've sent a generic blast. Personal touches in your messages dramatically improve response rates.

**Respect candidates' time.** Move quickly through your process, communicate clearly about next steps and timelines, and don't leave people hanging. Even candidates you don't hire will remember how you treated them.

**Build relationships, not just transactions.** The best recruiters think long-term. Today's connection becomes tomorrow's hire or referral source. Invest in genuine relationships with people.

**Use data to improve.** Track response rates to different messages, conversion rates through your pipeline, and where candidates drop off. Use those insights to continuously refine your approach.

Managing candidates well is both an art and a science. The tools help with the science part - tracking, organizing, analyzing. The art - building relationships, communicating effectively, making good judgment calls - that's all you. Metatalio gives you the platform to do both well.
`
      },
      {
        id: "candidate-engagement",
        title: "Candidate Engagement",
        content: `
Coming soon! Detailed guides on engaging with candidates, email campaigns, nurture sequences, and building long-term relationships will be available here.
        `
      }
    ]
  },
  {
    id: "dashboard",
    name: "Dashboard",
    icon: <ChartBarIcon className="w-6 h-6" />,
    description: "Central command center for recruitment metrics and pipeline overview",
    sections: [
      {
        id: "dashboard-overview",
        title: "Understanding Your Dashboard",
        content: `
# Dashboard User Guide

The Dashboard serves as your central command center for all recruitment activities in MetaTalio ATS. This is where you start each day to get an immediate view of your pipeline health, pending tasks, and key metrics.

## What is the Dashboard?

The Dashboard consolidates your most important recruitment data into a single, easy-to-scan view. Rather than navigating through multiple screens to check your progress, the Dashboard presents everything you need to know about your current recruiting activities.

### When to Use the Dashboard

**Start of each workday:** Review overnight activity, check pipeline health, and identify priority tasks.

**During client calls:** Quickly reference current metrics and pipeline status for specific jobs or overall performance.

**End of day reviews:** Assess daily progress and plan tomorrow's activities.

**Team meetings:** Share visual pipeline status and identify bottlenecks requiring team attention.

### Who Uses the Dashboard

**Recruiters** use the Dashboard to monitor their assigned requisitions, track candidate submissions through the pipeline, and identify which jobs need immediate attention.

**Hiring Managers** rely on the Dashboard to see overall team performance, monitor fill rates, and spot potential delays in the hiring process.

**Tenant Administrators** view organization-wide metrics, track platform adoption across teams, and identify system-wide trends that inform strategic decisions.

## Understanding Your Dashboard Layout

The Dashboard displays information in four main sections:

**Welcome Banner:** Shows your name, current time of day, and organization. This personalizes the experience and confirms you're in the correct tenant environment.

**KPI Cards (Top Row):** Four metric cards showing Requisitions, Candidates, Submissions, and Placements. Each card is clickable and takes you to the detailed view for that area.

**Pipeline Overview (Center):** A visual funnel showing how submissions move from initial submission to final placement. This helps you identify where candidates are getting stuck in your process.

**Activity Feed and Quick Actions (Bottom Row):** Recent activity appears on the left, while quick-access buttons for common tasks appear on the right.

## Reading Your KPI Cards

Each KPI card shows both current totals and actionable breakdowns to help you understand what needs attention.

### Requisitions Card

This card shows your job openings and their current status.

**What the numbers mean:**
- **Top number:** Total job requisitions in your system
- **Active:** Jobs actively recruiting (status = "Open")  
- **OnHold:** Jobs temporarily paused (status = "OnHold")

**What to look for:**
- **High Active count (30+):** You have strong hiring demand
- **High OnHold count:** Some jobs may have budget delays or changing requirements
- **Low overall count:** Consider reaching out to clients for additional job orders

**How to take action:**
Click the card to view all requisitions. You can then prioritize which open jobs need immediate candidate submissions and follow up on any jobs that have been on hold for extended periods.

### Candidates Card

This card shows your talent pool size and availability.

**What the numbers mean:**
- **Top number:** Total candidates in your database
- **Available:** Candidates ready for new job submissions (excludes those already placed, inactive, or marked "do not contact")

**What to look for:**
- **High total (1000+):** Strong candidate database
- **Low available percentage:** Many candidates are placed or inactive - time to source new talent
- **Growing numbers:** Healthy pipeline development

**How to take action:**
Click the card to search candidates. Use filters to find candidates matching your open requisitions, or identify candidates who need status updates.

### Submissions Card  

This card tracks how many candidates you've submitted to jobs and their current progress.

**What the numbers mean:**
- **Top number:** Total candidate submissions to jobs
- **Pending:** Submissions waiting for client review (statuses like "Submitted" or "Shortlisted")
- **Interviews:** Submissions currently in interview stages

**What to look for:**
- **High pending numbers:** Clients may need follow-up to review submissions
- **High interview numbers:** Strong pipeline with good conversion rates
- **Low overall numbers:** Increase submission activity to maintain pipeline flow

**How to take action:**
Click the card to view the submission pipeline. You can identify which submissions need client follow-up and which interviews need scheduling or feedback collection.

### Placements Card

This card shows your hiring success and recent performance trends.

**What the numbers mean:**
- **Top number:** Total successful placements
- **This Month:** Placements completed in the current month
- **Trend indicator:** Shows increase or decrease from the previous month

**What to look for:**
- **Positive monthly trend:** Improving performance
- **Low monthly numbers:** Pipeline may need acceleration
- **High placement rate:** Strong recruiting effectiveness

**How to take action:**
Click the card to view placed submissions. Review successful placements to understand what's working well, and identify patterns in your most successful hires.

## Understanding Your Pipeline Overview

The Pipeline Overview shows you how candidates flow through your hiring process. This visual funnel helps you quickly identify where candidates are getting stuck and where you need to take action.

### How to Read the Pipeline

The pipeline displays as a horizontal flow chart with numbers showing how many submissions are in each stage:

**Submitted → Shortlisted → Interview → Offered → Placed**
**156 → 89 → 45 → 23 → 12**

Each stage represents a step in your hiring process:

**Submitted:** New candidate submissions waiting for initial review. High numbers here suggest you need to prioritize reviewing new submissions.

**Shortlisted:** Candidates who passed initial screening and are qualified for the role. These are ready for interview scheduling or client presentation.

**Interview:** Candidates currently in the interview process (both internal and client interviews). Monitor this stage for scheduling delays.

**Offered:** Candidates who received job offers and are deciding whether to accept. Follow up timing is critical in this stage.

**Placed:** Successful hires who accepted offers and started work. This is your win column.

### What the Pipeline Tells You

**Healthy pipeline characteristics:**
- Steady numbers flowing from left to right
- No single stage holding too high a percentage of total submissions
- Regular movement from Offered to Placed

**Warning signs to watch for:**
- Large numbers stuck in Submitted (review bottleneck)
- High Offered numbers with low Placed (offer acceptance issues)
- Very low Interview numbers (client engagement problems)

### Taking Action Based on Pipeline Data

**If you see high numbers in Submitted:**
Review submitted candidates promptly to move them to Shortlisted or provide feedback if they're not a fit.

**If you see high numbers in Shortlisted:**
Focus on scheduling interviews or presenting candidates to clients to keep the pipeline moving.

**If you see high numbers in Interview with low Offered:**
Investigate interview feedback and candidate quality. You may need better candidate screening or interview preparation.

**If you see high numbers in Offered with low Placed:**
Review offer timing, compensation packages, or competitor activity that might be affecting acceptance rates.
        `
      },
      {
        id: "activity-feed",
        title: "Activity Feed & Quick Actions",
        content: `
# Activity Feed & Quick Actions

## Activity Feed: Stay Connected to What Matters

The Activity Feed keeps you updated on important changes across all your requisitions and submissions without having to check each one manually.

### What You'll See in the Feed

The feed shows recent activity across your entire workload, including:

**Submission Updates:** New submissions, status changes, interview schedules
**Client Activity:** Client feedback, interview requests, offer approvals  
**System Events:** Profile updates, document uploads, calendar changes
**Team Actions:** Activity from other team members on shared requisitions

### Reading the Activity Feed

Each entry shows you exactly what happened, who did it, and when:

\`\`\`
👤 John Smith submitted candidate Sarah Lee for JOB-001
   2 minutes ago

📋 New requisition JOB-042 created by Mike Johnson  
   15 minutes ago

✅ Candidate David Chen moved to "Client Interview" 
   for JOB-005 by Sarah Williams
   1 hour ago
\`\`\`

**Icons help you quickly identify activity types:**
- 👤 Candidate submissions
- 📋 New requisitions  
- ✅ Status changes
- 💬 Comments and feedback
- 📅 Interview scheduling

### Why the Activity Feed Helps You

**Never miss critical updates:** Important changes appear instantly without checking individual submissions.

**Prioritize your day:** See what needs immediate attention versus what can wait.

**Stay coordinated:** Track what your team members are doing on shared accounts.

**Respond quickly:** Catch time-sensitive items like interview confirmations or offer responses.

### Tips for Using the Activity Feed

**Check first thing in the morning** to see what happened overnight and prioritize your day.

**Review before client calls** to get updated on recent activity you can discuss.

**Use it as a backup** when you think you might have missed an important email notification.

**Click on any activity** to go directly to the related submission or requisition for more details.

## Quick Actions: Get Things Done Faster

The Quick Actions panel puts your most-used functions right at your fingertips so you don't have to navigate through multiple pages.

### Available Quick Actions

**Create New Submission:** Add a candidate to an open requisition instantly
**Schedule Interview:** Set up interviews without leaving the dashboard  
**Add New Candidate:** Import candidate profiles to your database
**Create Requisition:** Start a new job opening with pre-filled templates
**Send Update:** Quickly notify clients or candidates about status changes

### When to Use Quick Actions

**During busy periods:** When you need to accomplish multiple tasks quickly without navigation delays.

**Client calls:** When a client asks you to take immediate action while you're talking.

**End-of-day tasks:** To quickly complete final actions before wrapping up.

**Mobile work:** Quick actions work well on tablets when you're working remotely.

### Quick Action Tips

**Bookmark frequently used actions** by noting which ones save you the most time.

**Use keyboard shortcuts** when available to speed up repetitive tasks.

**Complete actions fully** rather than starting them and finishing later to avoid confusion.

## Dashboard Best Practices

### Daily Dashboard Routine

**Morning Review (5 minutes):**
- Check KPI cards for overnight changes
- Review Activity Feed for urgent items  
- Note pipeline bottlenecks from Pipeline Overview
- Plan your day based on what you see

**Midday Check (2 minutes):**
- Quick KPI review to track progress
- Check Activity Feed for new client responses
- Look for interview scheduling opportunities

**End of Day Review (3 minutes):**
- Update any submissions that need status changes
- Use Quick Actions to handle final tasks
- Review tomorrow's schedule and prepare

### Using Dashboard Data for Client Conversations

**Before client calls:**
- Pull up their specific requisitions from KPI cards
- Review recent activity to discuss current candidates
- Check pipeline status to set realistic expectations

**During client meetings:**
- Use KPI numbers to show progress and activity levels
- Reference pipeline data to explain next steps
- Use Quick Actions to make commitments in real-time

**After client meetings:**
- Update submission statuses based on feedback
- Use Activity Feed to confirm all changes were captured
- Set follow-up reminders for next touches

### Maximizing Dashboard Efficiency

**Customize your view** by focusing on the metrics that matter most for your role and accounts.

**Set up notifications** so the most critical changes reach you immediately without overwhelming your inbox.

**Use dashboard patterns** to spot trends - like seasonal hiring patterns or client response times.

**Share insights** with your team when dashboard data reveals opportunities or concerns that affect everyone.
        `
      }
    ]
  },
  {
    id: "settings",
    name: "Settings & Configuration",
    icon: <CogIcon className="w-6 h-6" />,
    description: "Personal preferences, tenant configuration, and user management",
    sections: [
      {
        id: "profile-settings",
        title: "Profile & Personal Settings",
        content: `
# Personal Profile Settings

Every user in MetaTalio can manage their personal profile settings, preferences, and notification options. This section covers everything you can customize for your own account.

## Personal Information

### Basic Details

Keep your contact information current so team members and the system can reach you when needed.

**Editable Information:**
- **First Name & Last Name:** Your display name throughout the system
- **Primary Phone:** Main contact number for important calls
- **Alternate Email:** Backup email for account recovery and notifications
- **Alternate Phone:** Secondary contact number

**System-Controlled Information:**
- **Email Address:** Your login email (contact support to change)
- **Account Role:** Set by your tenant administrator
- **Full Name:** Automatically generated from first and last name

### Address Information

Your address information helps with timezone detection and localization settings.

- **Street Address:** Include apartment or suite numbers in Address Line 2
- **City & State:** Used for timezone and regional settings
- **ZIP Code & Country:** Helps with localization and compliance features

## Personal Preferences

### Display & Interface

**Theme Selection:**
- **Light Mode:** Traditional light background with dark text
- **Dark Mode:** Dark background with light text, easier on eyes in low light
- **Auto Mode:** Follows your operating system preference

**Localization Settings:**
- **Timezone:** Critical for accurate scheduling and deadline tracking
- **Language:** Interface language (English, Spanish, French, German, Japanese, Chinese)
- **Date Format:** Choose from MM/DD/YYYY, DD/MM/YYYY, or other regional formats
- **Time Format:** 12-hour (2:30 PM) or 24-hour (14:30) display

### Communication Preferences

Control how and when you receive notifications about system activity.

**Email Notifications:**
- **Account Activity:** Login alerts, password changes, security events
- **Work Updates:** New assignments, status changes, deadline reminders
- **Team Activity:** Updates from colleagues on shared projects

**Browser Notifications:**
- **Real-time Alerts:** Instant notifications for urgent items
- **Desktop Notifications:** Pop-up alerts when MetaTalio isn't active

**Activity Digest:**
- **Daily Summary:** End-of-day email with activity summary
- **Weekly Digest:** Weekly overview of accomplishments and pending items
- **Never:** Disable digest emails completely

## Avatar & Profile Picture

### Uploading Your Photo

A professional photo helps team members recognize you and makes the platform more personal.

**Photo Requirements:**
- **File formats:** JPG, PNG, or GIF
- **File size:** Maximum 5MB
- **Recommended size:** 200x200 pixels minimum
- **Square format:** Works best for profile displays

**Upload Process:**
1. Click the avatar placeholder or existing photo
2. Choose file from your device
3. Adjust cropping if needed
4. Save changes

The system automatically resizes and optimizes your photo for different display contexts throughout the platform.

## Security & Privacy

### Password Management

While you can't change your password from the profile settings, you can initiate password resets and review security settings.

**Password Requirements:**
- Minimum 8 characters
- Must include uppercase, lowercase, and numbers
- Special characters recommended
- Cannot reuse last 3 passwords

### Session Management

Review and manage your active login sessions.

**Active Sessions:**
- See devices and locations where you're logged in
- Log out from specific devices if needed
- Review login history for security monitoring

### Data Privacy

Control how your information is used and shared within the platform.

**Profile Visibility:**
- **Team Members:** Your profile is visible to colleagues in your organization
- **Contact Information:** Phone and email are visible to team members who need to reach you
- **Activity History:** Your work activity is tracked for performance and security purposes

## Saving Changes

### Automatic vs Manual Save

**Automatic Save:** Most preference changes (theme, language, timezone) save immediately when selected.

**Manual Save:** Profile information changes require clicking "Save Changes" to confirm updates.

**Validation:** The system validates information (email format, phone number format) before saving.

### Change Notifications

When you update your profile:
- You receive a confirmation email for security-related changes
- Team members are notified if your contact information changes
- Your activity appears in team activity feeds for transparency

## Tips for Profile Management

### Keep Information Current

**Regular Review:** Check your profile monthly to ensure information is accurate, especially after moving or changing phone numbers.

**Professional Photo:** Use a current, professional headshot that helps colleagues recognize you in person.

**Timezone Accuracy:** Keep your timezone current, especially if you travel frequently or relocate.

### Notification Optimization

**Start Conservative:** Begin with minimal notifications and add more as needed rather than being overwhelmed initially.

**Context Switching:** Consider your work environment - open offices may benefit from fewer audible notifications.

**Mobile Coordination:** Coordinate your MetaTalio notification settings with your mobile device settings to avoid duplicates.

### Security Best Practices

**Regular Password Updates:** Even though you can't change passwords in-app, update them regularly through the security settings.

**Session Monitoring:** Review active sessions periodically and log out from devices you no longer use.

**Privacy Awareness:** Remember that profile information is visible to your team, so maintain professional standards.
        `
      },
      {
        id: "tenant-settings",
        title: "Tenant Configuration (Admin Only)",
        content: `
# Tenant Configuration Settings

Tenant settings control organization-wide preferences and are only accessible to users with tenant administrator privileges. These settings affect how the entire MetaTalio instance operates for your organization.

## Who Can Access Tenant Settings

**Required Permissions:**
- **TENANT_ADMIN role** - Full access to all tenant configuration
- **TENANT_SETTINGS_READ** - View tenant settings
- **TENANT_SETTINGS_UPDATE** - Modify tenant settings

## Tenant Profile Information

### Organization Details

**Read-Only Information:**
Your core organizational details are protected and can only be changed by contacting support:

- **Tenant Name:** Your organization's official name as it appears throughout the system
- **Short Code:** Unique identifier used in URLs and system-generated IDs (e.g., "ACME" for ACME Corporation)
- **Status:** Active/Inactive status of your tenant account
- **Created Date:** When your MetaTalio account was originally established

*To modify any of these details, contact our support team with verification of your authority to make organizational changes.*

### ID Prefix Customization

Customize how system-generated identifiers appear to match your organizational preferences.

**Available Prefixes:**
- **Job ID Prefix:** Default "JOB" (creates IDs like JOB-0001, JOB-0002)
- **Candidate ID Prefix:** Default "CAND" (creates IDs like CAND-0001)
- **Submission ID Prefix:** Default "SUB" (creates IDs like SUB-0001)
- **Vendor ID Prefix:** Default "VEN" (creates IDs like VEN-0001)

**Prefix Rules:**
- 2-8 characters maximum
- Letters and numbers only (no special characters)
- Uppercase recommended for consistency
- Cannot duplicate existing prefixes
- Changes only affect new records created after the change

**Example Customizations:**
- Technology company might use "TECH-0001" for jobs
- Healthcare organization might use "MED-0001" for candidates
- Consulting firm might use "PROJ-0001" for requisitions

## Workflow Configuration

### Requisition Status Management

Define the available statuses for job requisitions to match your hiring process.

**Default Statuses:**
- **Open:** Actively recruiting for this position
- **OnHold:** Temporarily paused (budget approval, requirements change, etc.)
- **Closed:** Successfully filled
- **Cancelled:** No longer hiring for this position

**Custom Statuses:**
Add organization-specific statuses like:
- **Pending Approval:** Waiting for budget or management approval
- **Draft:** Being prepared but not yet active
- **Urgent:** High-priority requisitions needing immediate attention

### Submission Pipeline Customization

Configure the stages candidates move through in your hiring process.

**Default Pipeline:**
1. **Submitted:** Initial candidate submission
2. **Shortlisted:** Passed initial screening
3. **PV Interview:** Internal/recruiter interview
4. **Client Interview:** Client-side interview
5. **Offered:** Job offer extended
6. **Placed:** Offer accepted, candidate hired
7. **Rejected:** Not selected for role

**Customization Options:**
- **Add stages:** Insert additional interview rounds, reference checks, or approval steps
- **Rename stages:** Use terminology that matches your process ("Phone Screen" vs "PV Interview")
- **Reorder stages:** Arrange to match your typical flow
- **Set requirements:** Define which stages require specific permissions or approval

**Example Customizations:**

*Technology Company:*
Submitted → Screened → Technical Test → Technical Interview → Team Interview → Offer → Hired

*Executive Search:*
Sourced → Qualified → Client Interview (Round 1) → Client Interview (Round 2) → Reference Check → Offer → Placed

### Approval Workflow Settings

Control when approvals are required in your recruitment process.

**Requisition Approval Workflow:**
- **Enabled:** New job requisitions require approval before going live
- **Disabled:** Requisitions are immediately active when created
- **Approval Chain:** Define who can approve requisitions (managers, department heads, finance)

**Submission Approval Workflow:**
- **Enabled:** Candidate submissions require approval before being sent to clients
- **Disabled:** Submissions are immediately active
- **Auto-approval Rules:** Set criteria for automatic approval (internal candidates, certain skill levels)

## Brand and Visual Customization

### Logo and Identity

Make MetaTalio reflect your organization's brand identity.

**Logo Upload:**
- **File Requirements:** PNG format with transparent background preferred
- **Size Recommendations:** 200x200 pixels minimum, square format works best
- **File Size Limit:** 2MB maximum
- **Usage:** Appears in header, emails, reports, and client-facing materials

**Color Customization:**
- **Primary Color:** Main brand color used for buttons, links, and key interface elements
- **Secondary Color:** Accent color for highlights and secondary elements
- **Color Format:** Use hex codes (#FF5733) or color picker for precise brand matching

**Preview Changes:**
The system provides real-time preview as you make changes, allowing you to see exactly how your customizations will appear before saving.

## Communication Settings

### Email Configuration

Configure how MetaTalio sends emails on behalf of your organization.

**SMTP Settings Required:**
- **SMTP Server:** Your email server address (e.g., smtp.gmail.com)
- **Port:** Usually 587 for TLS or 465 for SSL
- **Username:** Email account username (often your full email address)
- **Password:** Email password or app-specific password
- **Encryption:** Choose TLS (recommended) or SSL

**Email Templates:**
- **From Name:** How your organization name appears in recipient inboxes
- **Reply-to Address:** Where responses to system emails are sent
- **Footer Text:** Standard footer for all outgoing emails

**Testing Email Setup:**
After configuration, use the **Send Test Email** function to verify settings work correctly. The test email should arrive within a few minutes.

### Notification Defaults

Set organization-wide defaults for user notifications.

**Default Settings for New Users:**
- **Email Notifications:** Enabled/disabled by default
- **Push Notifications:** Browser notification defaults
- **Digest Frequency:** Default to daily, weekly, or disabled
- **Activity Types:** Which activities trigger notifications by default

## Data Management Settings

### Retention Policies

Configure how long different types of data are retained in the system.

**Candidate Data:**
- **Active Candidates:** Maintain indefinitely while relationship is active
- **Inactive Candidates:** Auto-archive after specified period of inactivity
- **Placed Candidates:** Retain for specified period after placement
- **Withdrawn Candidates:** Retain for compliance period, then allow deletion

**Document Retention:**
- **Resumes:** Maintain with candidate records
- **Interview Notes:** Retain per company policy
- **Reference Documents:** Maintain for legal compliance periods
- **Offer Letters:** Retain per legal requirements

### Privacy and Compliance

Configure settings to meet regulatory requirements.

**GDPR Compliance:**
- **Enable GDPR Features:** Adds consent tracking and right-to-be-forgotten capabilities
- **Data Export Requests:** Allow candidates to request their data
- **Consent Management:** Track and store candidate consent for data processing
- **Deletion Workflow:** Process for handling data deletion requests

**Regional Compliance:**
- **Data Residency:** Ensure data stays within required geographic boundaries
- **Audit Trail:** Maintain detailed logs of all data access and modifications
- **Backup Policies:** Configure backup retention to meet compliance needs

## Integration Configuration

### API Access

Configure external system integrations and API access.

**API Keys:**
- **Generate API Keys:** Create secure keys for system integrations
- **Scope Permissions:** Limit what each API key can access
- **Monitor Usage:** Track API usage and performance
- **Rotate Keys:** Regular security key rotation

**Webhook Configuration:**
- **Event Triggers:** Choose which events send webhook notifications
- **Endpoint URLs:** Configure where webhooks are sent
- **Authentication:** Secure webhook delivery with tokens or signatures
- **Retry Logic:** Configure retry attempts for failed deliveries

### Third-Party Integrations

Configure connections to external systems.

**Supported Integrations:**
- **Email Systems:** Connect to existing email platforms
- **Calendar Systems:** Integrate with organizational calendar systems
- **HRIS Systems:** Connect to human resource information systems
- **Background Check Services:** Integrate screening and verification services

## Saving and Managing Changes

### Change Management Process

**Preview Changes:** Many settings provide preview functionality before committing changes.

**Staged Rollout:** Some changes can be tested with a subset of users before organization-wide deployment.

**Change Approval:** Critical settings changes may require additional approval from multiple administrators.

### Backup and Recovery

**Configuration Backup:** Critical settings are automatically backed up before changes.

**Rollback Capability:** Most configuration changes can be reverted if issues arise.

**Change History:** Complete audit trail of all configuration changes with timestamps and user identification.

## Getting Help

### Documentation and Support

**Hover Help:** Most settings include tooltip explanations when you hover over the information icon.

**Support Chat:** Access live support directly from the settings interface for complex configuration questions.

**Implementation Support:** For major configuration changes, our implementation team can provide guided assistance.

### Best Practices

**Start Simple:** Begin with basic configuration and add complexity gradually as your team becomes comfortable with the platform.

**Test Changes:** Use preview and testing features before deploying changes organization-wide.

**Document Decisions:** Keep internal notes about why specific configurations were chosen to help future administrators.

**Regular Review:** Periodically review settings to ensure they still match your evolving organizational needs.
        `
      }
    ]
  },
  {
    id: "analytics",
    name: "Analytics & Reporting",
    icon: <ChartBarIcon className="w-6 h-6" />,
    description: "Performance metrics, reporting dashboards, and data insights",
    sections: [
      {
        id: "analytics-overview",
        title: "Analytics Overview",
        content: `
# Analytics & Reporting User Guide

The Analytics and Reporting module in MetaTalio provides comprehensive insights into your recruitment operations through interactive dashboards, detailed reports, and intelligent data analysis.

## Purpose of Analytics

### Business Intelligence

Track key performance indicators (KPIs) in real-time to understand how your recruitment operations are performing:

- **Pipeline Health:** Monitor how candidates flow through your hiring process
- **Performance Metrics:** Track individual and team success rates
- **Efficiency Analysis:** Identify bottlenecks and optimization opportunities
- **ROI Measurement:** Understand the return on investment for different recruitment strategies

### Operational Insights

Get actionable insights that help you make informed decisions:

- **Time-to-Fill Metrics:** Understand how long positions take to fill
- **Conversion Rates:** See how well candidates progress through your pipeline
- **Source Analysis:** Identify which candidate sources provide the best results
- **Capacity Planning:** Use historical data to plan future resource needs

## Analytics Architecture

### Seven Core Report Types

MetaTalio provides seven different analytical views, each designed for specific insights:

1. **Overview Dashboard** - Executive-level KPIs and high-level insights
2. **Recruiters Report** - Individual recruiter performance and activity metrics  
3. **Requisitions Report** - Job posting analytics and aging analysis
4. **Candidates Report** - Talent pipeline and sourcing effectiveness
5. **Submissions Report** - Candidate submission lifecycle and conversion tracking
6. **Vendors Report** - External vendor performance analysis (tenant-only access)
7. **Pipeline Report** - Visual pipeline flow with detailed stage analysis

### Two Analytical Approaches

**Interactive Dashboards:**
- Real-time widgets with customizable layouts
- Drag-and-drop arrangement for personalized views
- Live data updates every 30 seconds
- Filter controls that affect all dashboard widgets
- Multiple widget types: KPI cards, charts, tables, heatmaps, calendars

**Detailed Drill-Down Reports:**
- Comprehensive data tables with advanced filtering
- Export functionality (Excel, CSV, PDF)
- Historical trend analysis and period comparisons
- Smart insights with automatically generated recommendations
- Deep-dive capability from high-level metrics to individual records

## Role-Based Analytics Access

### Permission Structure

Analytics access is controlled by role-based permissions:

- **REPORTS_READ** - View all reporting dashboards
- **REPORTS_EXPORT** - Export data to Excel/CSV formats  
- **ANALYTICS_READ** - Access interactive analytics dashboards
- **ANALYTICS_EXPORT** - Export analytics data and charts

### Data Scoping

Different user types see different data views:

- **Tenant Users** - Access to all organizational data across teams
- **Vendor Users** - Limited to only their submitted candidates and related data
- **Recruiters** - Can see own data plus team data (configurable by administrators)
- **Managers** - Team and departmental data for people they supervise

## Dashboard vs Reports: When to Use Each

### Use Dashboards When:

- **Quick daily check-ins** - Get immediate pulse on key metrics
- **Live monitoring** - Watch real-time activity during busy periods
- **Executive briefings** - Present high-level status to leadership
- **Trend spotting** - Identify patterns in performance over time

### Use Drill-Down Reports When:

- **Deep analysis** - Investigate specific performance issues
- **Compliance reporting** - Generate detailed reports for stakeholders
- **Data export** - Extract data for external analysis or presentation
- **Historical research** - Analyze trends over extended periods

## Smart Insights Feature

### Automated Intelligence

MetaTalio automatically analyzes your data and surfaces important insights:

**Performance Alerts:**
- "Placement rate dropped 15% this month compared to last month"
- "Average time-to-fill increased from 18 days to 24 days"
- "Three requisitions have been open over 60 days"

**Opportunity Identification:**
- "Top performing recruiter has 40% higher submission-to-interview rate"
- "Candidates from LinkedIn have 2x higher placement rate than other sources"
- "Wednesday submissions have highest client response rate"

**Trend Analysis:**
- "Hiring activity typically increases 30% in Q1"
- "Engineering roles take 50% longer to fill than other positions"
- "Direct applications have higher success rate than sourced candidates"

### Acting on Insights

Each insight includes:
- **Context:** Why this insight is important
- **Recommendation:** Suggested action to take
- **Deep Dive:** Link to detailed report for further analysis
- **Tracking:** Monitor how changes affect the metric over time

## Calendar & Heatmap Analytics

### Activity Heatmaps

Visual representations of activity patterns across time:

**Daily Activity Heatmap:**
- Shows activity intensity across hours of the day
- Identifies peak productivity periods
- Helps optimize scheduling and outreach timing

**Weekly Activity Heatmap:**  
- Displays activity patterns across days of the week
- Reveals seasonal or cyclical trends
- Assists in capacity and resource planning

**Monthly Activity Calendar:**
- Color-coded calendar showing activity levels by day
- Quickly identifies busy periods and quiet times
- Useful for planning team schedules and deadlines

### Calendar Integration Analytics

**Interview Scheduling Analytics:**
- Most effective interview time slots
- Average time from invitation to scheduled interview
- No-show rates by time of day or day of week
- Interviewer availability patterns

**Response Time Analysis:**
- How quickly clients respond to submissions
- Optimal follow-up timing for maximum response rates
- Communication pattern analysis for better engagement timing

## Exporting and Sharing Reports

### Export Options

**Excel Format:**
- Formatted workbook with multiple sheets
- Charts and graphs included
- Ready for further analysis or presentation

**CSV Format:**
- Raw data export for database import
- Suitable for custom analysis tools
- Lightweight format for large datasets

**PDF Format:**
- Professional presentation format
- Includes charts, graphs, and formatting
- Perfect for stakeholder reports and documentation

### Scheduled Reports

**Automated Delivery:**
- Set up regular report delivery via email
- Choose frequency: daily, weekly, monthly, quarterly
- Customize recipient lists and report content
- Include personalized commentary and context

**Report Subscriptions:**
- Team members can subscribe to relevant reports
- Automatic updates when new data becomes available
- Personalized insights based on individual roles and responsibilities

## Best Practices for Analytics Usage

### Daily Analytics Routine

**Morning Review:**
- Check overnight activity on Overview Dashboard
- Review any smart insights or alerts
- Identify priority items requiring immediate attention

**Throughout the Day:**
- Use real-time dashboards during client calls
- Monitor pipeline flow for bottlenecks
- Track progress against daily and weekly goals

**End of Day:**
- Review accomplishments against targets
- Identify patterns in successful activities
- Plan next day priorities based on data trends

### Strategic Analytics Usage

**Monthly Analysis:**
- Deep dive into performance trends
- Compare results against historical data  
- Identify improvement opportunities
- Plan resource allocation for following month

**Quarterly Planning:**
- Analyze seasonal patterns and trends
- Review goal achievement and adjust targets
- Assess tool and process effectiveness
- Plan strategic initiatives based on data insights

### Team Analytics Culture

**Data-Driven Decisions:**
- Use analytics to support recommendations
- Share insights that benefit entire team
- Challenge assumptions with factual data
- Celebrate wins visible in the metrics

**Continuous Improvement:**
- Regularly review which metrics matter most
- Adjust tracking and reporting based on business needs
- Train team members on effective analytics usage
- Create feedback loops for process improvement
        `
      },
      {
        id: "dashboard-widgets",
        title: "Dashboard Widgets & Reports",
        content: `
# Dashboard Widgets & Detailed Reports

## Interactive Dashboard Widgets

### Widget Types Available

**KPI Cards:**
Display single metrics with trend indicators and quick comparison data.
- **Current Value:** The main metric being tracked
- **Trend Direction:** Up/down arrows showing direction of change
- **Percentage Change:** Numerical change from previous period
- **Click Action:** Navigate to detailed view

**Chart Widgets:**
Visual representations of data trends and comparisons.
- **Line Charts:** Track metrics over time (submissions per week, placements per month)
- **Bar Charts:** Compare different categories (performance by recruiter, activity by department)  
- **Pie Charts:** Show proportional data (candidate sources, submission statuses)
- **Funnel Charts:** Visualize conversion through pipeline stages

**Table Widgets:**
Structured data displays with sorting and filtering capabilities.
- **Top Performers:** Ranked lists of recruiters, vendors, or requisitions
- **Recent Activity:** Latest actions taken across the platform
- **Aging Reports:** Items requiring attention based on time thresholds

**Heatmap Widgets:**
Show activity intensity across time periods.
- **Daily Activity:** Hours with highest activity levels
- **Weekly Patterns:** Days of week with peak performance
- **Monthly Trends:** Seasonal variations in hiring activity

**Calendar Widgets:**
Time-based activity visualization.
- **Interview Calendar:** Upcoming and completed interviews
- **Placement Calendar:** Successful hires by date
- **Activity Calendar:** Overall platform usage by day

### Widget Customization

**Drag-and-Drop Layout:**
- Rearrange widgets to match your workflow priorities
- Resize widgets to emphasize most important metrics
- Create multiple dashboard views for different contexts (daily operations, weekly reviews, monthly planning)

**Filter Controls:**
Global dashboard filters affect all widgets simultaneously:
- **Date Range:** Last 7 days, 30 days, quarter, year, or custom range
- **Team/Individual:** View personal metrics or team aggregates  
- **Department:** Focus on specific business units or client accounts
- **Status:** Filter by requisition status, candidate status, etc.

**Personalization:**
- Save multiple dashboard configurations
- Set default views for different scenarios
- Choose which metrics appear in each widget
- Configure alert thresholds for important KPIs

## Seven Core Reports

### 1. Overview Dashboard

**Purpose:** Executive-level insights and organizational KPIs.

**Key Metrics:**
- Total active requisitions and hiring demand
- Candidate pipeline health and capacity
- Team performance and productivity indicators
- Financial metrics (cost-per-hire, time-to-fill)

**Insights Provided:**
- Overall recruitment health score
- Bottlenecks requiring management attention
- Resource allocation recommendations
- Performance against organizational goals

**Best Used By:** Executives, Department Heads, Recruitment Managers

### 2. Recruiters Report

**Purpose:** Individual and team performance analytics.

**Key Metrics:**
- Candidates sourced and submissions created
- Interview scheduling and conversion rates
- Placement success and quality indicators
- Activity levels and productivity measures

**Insights Provided:**
- Top performers and improvement opportunities
- Training needs identification
- Workload distribution analysis
- Goal achievement tracking

**Best Used By:** Recruitment Managers, Individual Recruiters, HR Teams

### 3. Requisitions Report

**Purpose:** Job posting performance and aging analysis.

**Key Metrics:**
- Requisition lifecycle from posting to closure
- Fill rates and time-to-fill analysis
- Client engagement and response patterns
- Priority job performance tracking

**Insights Provided:**
- Difficult-to-fill position identification
- Client satisfaction indicators
- Pricing and competitiveness analysis
- Resource requirement forecasting

**Best Used By:** Account Managers, Recruitment Managers, Business Development

### 4. Candidates Report

**Purpose:** Talent pipeline and sourcing effectiveness analysis.

**Key Metrics:**
- Candidate source effectiveness (job boards, referrals, direct sourcing)
- Pipeline conversion rates from initial contact to placement
- Candidate quality indicators and feedback analysis
- Database growth and talent pool health

**Insights Provided:**
- Most effective sourcing channels
- Candidate engagement optimization opportunities  
- Quality vs. quantity sourcing analysis
- Long-term relationship building effectiveness

**Best Used By:** Sourcers, Recruiters, Talent Acquisition Teams

### 5. Submissions Report

**Purpose:** Candidate submission lifecycle and conversion tracking.

**Key Metrics:**
- Submission-to-interview conversion rates
- Interview-to-offer progression analysis  
- Client feedback patterns and response timing
- Stage-specific performance and bottlenecks

**Insights Provided:**
- Pipeline optimization opportunities
- Client relationship health indicators
- Process efficiency improvements
- Quality control and feedback integration

**Best Used By:** Recruiters, Account Managers, Process Improvement Teams

### 6. Vendors Report

**Purpose:** External vendor performance analysis (tenant-only access).

**Key Metrics:**
- Vendor submission quality and quantity
- Conversion rates and placement success
- Compliance and documentation tracking
- Cost-effectiveness and ROI analysis

**Insights Provided:**
- Top performing vendor relationships
- Quality improvement opportunities
- Contract and performance management insights
- Strategic vendor partnership decisions

**Best Used By:** Vendor Managers, Procurement Teams, Executive Leadership

### 7. Pipeline Report

**Purpose:** Visual pipeline flow with detailed stage analysis.

**Key Metrics:**
- Stage-by-stage conversion rates
- Average time spent in each pipeline stage
- Bottleneck identification and flow analysis
- Comparison across teams, clients, and time periods

**Insights Provided:**
- Process optimization recommendations
- Stage-specific improvement opportunities
- Capacity planning and resource allocation
- Client-specific pipeline performance

**Best Used By:** Operations Teams, Process Managers, Recruitment Leadership

## Smart Insights and Recommendations

### Automated Analysis

**Pattern Recognition:**
The system continuously analyzes your data to identify patterns:
- "Submissions on Tuesdays have 25% higher interview rates"
- "Engineering candidates require average 3.2 interviews vs 2.1 for other roles"
- "Referral candidates have 60% higher placement rates"

**Performance Alerts:**
Automatic notifications when metrics deviate from normal ranges:
- "Placement rate dropped below 15% threshold"
- "Average time-to-fill increased by 40% this month"
- "Client response rate decreased for ABC Corporation"

**Opportunity Identification:**
Proactive suggestions for improvement:
- "Consider focusing on LinkedIn sourcing - 2x higher conversion rate"
- "Wednesday 2-4 PM shows highest client response rates"
- "Java developers consistently exceed salary expectations by 15%"

### Acting on Insights

**Contextual Recommendations:**
Each insight includes specific suggested actions:
- **Process Changes:** Modify workflows based on performance data
- **Resource Allocation:** Redistribute efforts to higher-performing activities
- **Training Needs:** Identify skill gaps requiring development
- **Client Management:** Adjust approach based on response patterns

**Implementation Tracking:**
Monitor the impact of changes made based on insights:
- **Before/After Analysis:** Compare metrics pre and post implementation
- **Progress Tracking:** Monitor improvement trends over time
- **ROI Measurement:** Quantify benefits of insight-driven changes
- **Continuous Optimization:** Iterative improvement based on results

## Export and Integration Capabilities

### Export Formats

**Excel Workbooks:**
- Multiple sheets organized by report type
- Formatted tables with headers and styling
- Embedded charts and graphs
- Ready for presentation or further analysis

**CSV Data Files:**
- Raw data export for database import
- Suitable for custom analysis tools
- Lightweight format for large datasets
- Easy integration with external systems

**PDF Reports:**
- Professional formatted reports
- Include executive summaries and key insights
- Charts, graphs, and visual elements preserved
- Suitable for stakeholder distribution

### Integration Options

**API Access:**
- Real-time data access for external systems
- Custom dashboard development
- Integration with business intelligence tools
- Automated data synchronization

**Webhook Notifications:**
- Real-time alerts to external systems
- Custom triggers based on specific metrics
- Integration with workflow automation tools
- Event-driven data processing

**Scheduled Exports:**
- Automated report generation and delivery
- Customizable frequency and recipients
- Integration with email and file sharing systems
- Consistent stakeholder communication
        `
      }
    ]
  },
  {
    id: "candidate-engagement",
    name: "Candidate Engagement",
    icon: <UserGroupIcon className="w-6 h-6" />,
    description: "Engaging with candidates and building long-term relationships",
    sections: [
      {
        id: "engagement-overview",
        title: "Candidate Engagement Overview",
        content: `
Coming soon! Detailed guides on engaging with candidates, email campaigns, nurture sequences, and building long-term relationships will be available here.
        `
      }
    ]
  }
];

const DocsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(modules[0]);
  const [selectedSection, setSelectedSection] = useState(modules[0].sections[0]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = modules.map(category => ({
    ...category,
    sections: category.sections.filter(section => {
      const titleMatch = section.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const contentMatch = section.content?.toLowerCase().includes(searchQuery.toLowerCase());
      return titleMatch || contentMatch;
    })
  })).filter(category => category.sections.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="mx-auto">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <BookOpenIcon className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Documentation</h1>
              <p className="text-slate-600 mt-1">Everything you need to know about using Metatalio</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-6 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all"
            />
          </div>
        </div>
      </div>

      <div className="flex">{/* Sidebar */}
        <div className="w-72 bg-white border-r border-slate-200 min-h-screen p-6 sticky top-0 overflow-y-auto max-h-screen">
          <nav className="space-y-1">
            {filteredCategories.map((category) => (
              <div key={category.id} className="mb-6">
                <button
                  onClick={() => {
                    setSelectedCategory(category);
                    setSelectedSection(category.sections[0]);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    selectedCategory.id === category.id
                      ? 'bg-slate-100 text-slate-900'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {category.icon}
                  <div className="flex-1 text-left">
                    <div className="font-semibold">{category.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{category.description}</div>
                  </div>
                </button>

                {selectedCategory.id === category.id && (
                  <div className="mt-2 ml-4 space-y-1">
                    {category.sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => setSelectedSection(section)}
                        className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
                          selectedSection.id === section.id
                            ? 'bg-slate-100 text-slate-900 font-medium'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <div className="prose prose-slate max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => <h1 className="text-4xl font-bold text-slate-900 mb-6 pb-4 border-b border-slate-200" {...props} />,
                  h2: ({ node, ...props }) => <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-4" {...props} />,
                  h3: ({ node, ...props }) => <h3 className="text-2xl font-semibold text-slate-800 mt-8 mb-3" {...props} />,
                h4: ({ node, ...props }) => <h4 className="text-xl font-semibold text-slate-800 mt-6 mb-2" {...props} />,
                p: ({ node, ...props }) => <p className="text-slate-600 leading-relaxed mb-4" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal list-inside text-slate-600 space-y-2 mb-4" {...props} />,
                li: ({ node, ...props }) => <li className="text-slate-600" {...props} />,
                code: ({ node, inline, ...props }) => 
                  inline ? (
                    <code className="px-2 py-1 bg-slate-100 text-slate-800 rounded text-sm font-mono" {...props} />
                  ) : (
                    <code className="block p-4 bg-slate-900 text-slate-100 rounded-lg text-sm font-mono overflow-x-auto mb-4" {...props} />
                  ),
                blockquote: ({ node, ...props }) => (
                  <blockquote className="border-l-4 border-slate-400 pl-4 italic text-slate-600 my-4" {...props} />
                ),
                img: ({ node, ...props }) => (
                  <img className="rounded-lg shadow-md my-6 w-full" {...props} />
                ),
                a: ({ node, ...props }) => (
                  <a className="text-slate-700 hover:text-slate-900 underline font-medium" {...props} />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-6">
                    <table className="min-w-full divide-y divide-slate-200" {...props} />
                  </div>
                ),
                thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
                tbody: ({ node, ...props }) => <tbody className="bg-white divide-y divide-slate-200" {...props} />,
                tr: ({ node, ...props }) => <tr {...props} />,
                th: ({ node, ...props }) => (
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" {...props} />
                ),
                td: ({ node, ...props }) => (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600" {...props} />
                ),
              }}
            >
              {selectedSection.content}
            </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;
